import { useCallback, useRef } from 'react';
import useWebSocketBase from 'react-use-websocket';

import { GetWebsocketUrl } from '../../../util';

import debounce from 'lodash.debounce';
import { throttle } from 'lodash';

type WebSocketOptions<T> = {
  onMessage?: (message: T) => unknown;
  debounceTime?: number; // Add debounce time option
  throttleTime?: number; // Add throttle time option
};

const useWebSocket = <T>(url: string, options?: WebSocketOptions<T>) => {
  const webSocketUrl = GetWebsocketUrl(url);
  const debounceTimeRef = useRef<number>(options?.debounceTime || 300);

  const socket = useWebSocketBase(webSocketUrl, {
    filter: () => false, // prevents re-render on messages
    share: true,

    shouldReconnect: (e) => {
      // https://developer.mozilla.org/en-US/docs/Web/API/CloseEvent/code
      if (e.code === 1006) {
        // looks like code 1006 happens when the API closes the connection
        return true;
      }
      return false;
    },

    onMessage: ({ data }) => {
      if (options?.onMessage) {
        const message = JSON.parse(data);
        options.onMessage(message);
      }
    },

    onError: (e) => {
      //  console.error(e);
      return;
    },
  });

  // Create a non-debounced send function that includes the timestamp
  const sendImmediately = useCallback((message: T) => {
    const timestamp = new Date().getTime() * 1_000_000;
    socket.sendJsonMessage({ timestamp, ...message });
  }, [socket]);

  // Create the debounced function once during hook initialization
  const debouncedSend = useCallback(
    debounce(sendImmediately, debounceTimeRef.current),
    [] // Empty dependency array means this is created once
  );

  const send = useCallback((message: T) => {
    debouncedSend(message);
  }, [debouncedSend]);

  const throttleSend = useCallback(throttle(sendImmediately, options?.throttleTime ?? 100), []);

  return { send, throttleSend, sendImmediately };
};

export default useWebSocket;